import type { ActionButton } from '@pesto/ui/features/CreditApplication/components/OverviewDetails';

import { CheckIcon } from '@heroicons/react/20/solid';
import { CrossCircledIcon } from '@radix-ui/react-icons';
import { RefreshCwIcon } from 'lucide-react';

import { CollateralType, CreditApplicationStatus } from '../../../__generated__/graphql/api';
import { ApplicationActions } from './useApplicationModal';

interface MainActionButtonsRenderOptions {
  status: CreditApplicationStatus | null | undefined;
  collateralType?: CollateralType | null;
}

export const useCreditApplicationDetails = () => {
  const mainActionButtons = {
    close: {
      label: 'Close application',
      action: ApplicationActions.CLOSE_APPLICATION,
      variant: 'outline',
      color: 'destructive',
      icon: CrossCircledIcon,
      sort: 1,
    },
    sendOffer: {
      label: 'Send Offer',
      action: ApplicationActions.SEND,
      variant: 'primary',
      icon: CheckIcon,
      sort: 2,
    },
    approve: {
      label: 'Approve application',
      action: ApplicationActions.APPROVE,
      variant: 'primary',
      icon: CheckIcon,
      sort: 3,
    },
    denyPartnerKyc: {
      label: 'Deny Partner KYC',
      action: ApplicationActions.DENY_PARTNER_KYC,
      variant: 'outline',
      color: 'destructive',
      icon: CrossCircledIcon,
      sort: 4,
    },
    retryPartnerKyc: {
      label: 'Retry KYC',
      action: ApplicationActions.RETRY_PARTNER_KYC,
      variant: 'outline',
      icon: RefreshCwIcon,
      sort: 2.5,
    },
  } satisfies Record<string, ActionButton>;

  const mainActionButtonsRender = ({ status, collateralType }: MainActionButtonsRenderOptions): ActionButton[] => {
    if (!status) return [];

    const isPartnerApp = collateralType === CollateralType.External;

    if (isPartnerApp) {
      if (status === CreditApplicationStatus.ManualReview) {
        return [mainActionButtons.retryPartnerKyc, mainActionButtons.approve, mainActionButtons.denyPartnerKyc];
      }

      return [];
    }

    const displayMainActionButtons: Partial<Record<CreditApplicationStatus, ActionButton[]>> = {
      [CreditApplicationStatus.ManualReview]: [mainActionButtons.approve],
    };

    const defaultButtons: ActionButton[] = [mainActionButtons.close, mainActionButtons.sendOffer];
    const buttons = displayMainActionButtons[status];

    return defaultButtons.concat(buttons ?? []).sort((a, b) => a.sort - b.sort);
  };

  const applicationActionButtons = [
    {
      label: 'Force Doc Check',
      action: ApplicationActions.FORCE_DOC_CHECK,
    },
    {
      label: 'Force Payment Check',
      action: ApplicationActions.FORCE_PAYMENT_CHECK,
    },
    {
      label: 'Run Soft Credit Check',
      action: ApplicationActions.RUN_SOFT_CREDIT_CHECK,
    },
    {
      label: 'Run Hard Credit Check (Maybe Card Order)',
      action: ApplicationActions.RUN_HARD_CREDIT_CHECK_MAYBE_CARD_ORDER,
    },
  ];

  return {
    mainActionButtonsRender,
    applicationActionButtons,
  };
};
